import { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Progress } from "./ui/progress";
import { Award, CheckCircle2, Loader2, Package as PackageIcon, PlayCircle, Circle } from "lucide-react";

/**
 * LearningPathModal — Modal "Mi trayecto formativo" (roadmap horizontal).
 *
 * Two modes:
 *   • self (default)  → fetches GET /me/learning-path
 *   • admin           → fetches GET /admin/users/{userId}/learning-path
 *
 * Props:
 *   open        boolean
 *   onOpenChange fn(boolean)
 *   variant     "self" | "admin"
 *   userId      required when variant === "admin"
 *   userLabel   optional label shown in the title (admin variant)
 */
export default function LearningPathModal({ open, onOpenChange, variant = "self", userId, userLabel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError("");
    const url = variant === "admin" ? `/admin/users/${userId}/learning-path` : "/me/learning-path";
    api
      .get(url)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "No se pudo cargar el trayecto"))
      .finally(() => setLoading(false));
  }, [open, variant, userId]);

  const title = variant === "admin"
    ? `Trayecto de ${userLabel || data?.user?.name || "alumno"}`
    : "Mi trayecto formativo";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid="learning-path-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{title}</DialogTitle>
          <DialogDescription>
            Vista general de tu progreso: cursos completados, horas lectivas y camino recorrido.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Cargando trayecto…
          </div>
        )}

        {error && !loading && (
          <div className="py-6 text-center text-sm text-red-600" data-testid="learning-path-error">
            {error}
          </div>
        )}

        {data && !loading && !error && <LearningPathBody data={data} />}
      </DialogContent>
    </Dialog>
  );
}

function LearningPathBody({ data }) {
  const { stats, steps, packages, is_package_owner: hasPackage } = data;

  if (!steps || steps.length === 0) {
    return (
      <div className="py-10 text-center" data-testid="learning-path-empty">
        <div className="w-16 h-16 rounded-full bg-gray-100 mx-auto mb-4 flex items-center justify-center">
          <Award className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-600">Aún no hay cursos asignados en este trayecto.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {hasPackage && packages.length > 0 && (
        <div
          className="rounded-lg border border-[#e7d5b4] bg-[#FBF8F2] p-4 flex items-start gap-3"
          data-testid="learning-path-package-banner"
        >
          <PackageIcon className="w-5 h-5 text-[#8a6f43] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#8a6f43] font-mono-data">
              Paquete formal
            </p>
            <p className="text-sm text-brand-ink font-medium">
              {packages.map((p) => p.name).join(" · ")}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              Este trayecto forma parte de un paquete cerrado que has adquirido.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Progreso"
          value={`${stats.overall_percent}%`}
          testid="stat-overall-percent"
          extra={<Progress value={stats.overall_percent} className="mt-2 h-1.5" />}
        />
        <StatCard
          label="Cursos"
          value={`${stats.passed_courses}/${stats.total_courses}`}
          testid="stat-courses"
        />
        <StatCard
          label="Horas lectivas"
          value={fmtHours(stats.hours_completed)}
          hint={`de ${fmtHours(stats.total_hours)} h`}
          testid="stat-hours"
        />
        <StatCard
          label="Pendientes"
          value={String(stats.total_courses - stats.passed_courses)}
          testid="stat-pending"
        />
      </div>

      <div>
        <p className="text-[10px] tracking-[0.2em] uppercase text-gray-500 font-mono-data mb-3">
          Roadmap
        </p>
        <div
          className="flex md:flex-wrap items-stretch gap-0 overflow-x-auto md:overflow-visible pb-4"
          data-testid="learning-path-steps"
        >
          {steps.map((s, idx) => (
            <StepItem key={s.assignment_id} step={s} index={idx} isLast={idx === steps.length - 1} />
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, hint, testid, extra }) {
  return (
    <div
      className="rounded-lg border border-gray-200 bg-white p-3"
      data-testid={testid}
    >
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className="font-display text-2xl text-brand-ink mt-1">{value}</p>
      {hint && <p className="text-[11px] text-gray-500">{hint}</p>}
      {extra}
    </div>
  );
}

function StepItem({ step, index, isLast }) {
  const kindLabel = step.kind === "final" ? "★ Curso" : "Taller";
  const styles = statusStyles(step.status);
  return (
    <div className="flex items-start flex-shrink-0" data-testid={`step-${index}`}>
      <div className="flex flex-col items-center w-32 md:w-36">
        <div
          className={`w-11 h-11 rounded-full border-2 flex items-center justify-center font-mono-data text-sm font-semibold ${styles.circle}`}
          title={styles.label}
        >
          {styles.icon || (index + 1)}
        </div>
        <p
          className={`mt-2 text-xs text-center line-clamp-2 min-h-[2.5rem] px-1 ${styles.textStrong}`}
          title={step.title}
        >
          {step.title}
        </p>
        <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{kindLabel}</p>
        {step.best_score !== null && step.best_score !== undefined && (
          <p className={`text-[11px] mt-0.5 font-mono-data ${step.passed ? "text-emerald-600" : "text-gray-500"}`}>
            {Math.round(step.best_score)}%
          </p>
        )}
      </div>
      {!isLast && (
        <div className="hidden md:block flex-shrink-0 mt-5">
          <div className={`h-0.5 w-6 ${styles.connector}`} />
        </div>
      )}
    </div>
  );
}

function statusStyles(status) {
  if (status === "passed") {
    return {
      circle: "bg-emerald-500 border-emerald-500 text-white",
      textStrong: "text-brand-ink font-medium",
      connector: "bg-emerald-400",
      icon: <CheckCircle2 className="w-5 h-5" />,
      label: "Aprobado",
    };
  }
  if (status === "in_progress") {
    return {
      circle: "bg-blue-500 border-blue-500 text-white",
      textStrong: "text-brand-ink",
      connector: "bg-blue-300",
      icon: <PlayCircle className="w-5 h-5" />,
      label: "En curso",
    };
  }
  return {
    circle: "bg-white border-gray-300 text-gray-400",
    textStrong: "text-gray-600",
    connector: "bg-gray-200",
    icon: <Circle className="w-4 h-4" />,
    label: "Sin empezar",
  };
}

function fmtHours(v) {
  const n = Number(v || 0);
  return Number.isInteger(n) ? `${n} h` : `${n.toFixed(1)} h`;
}
